import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { callFunction } from "../lib/callFunction.js";
import { nextDay, sydneyMidnightIso } from "../lib/sydneyDate.js";
import { formatDateTime, formatDate } from "../lib/datetime.js";

interface BusinessRow {
  id: string;
  name: string;
}

interface SyncStateRow {
  business_id: string;
  last_synced_at: string | null;
  last_sync_status: "ok" | "failed" | null;
  last_sync_error: string | null;
}

interface RunRow {
  id: string;
  started_at: string;
  finished_at: string | null;
  triggered_by: string;
  custom_range: boolean;
  status: "running" | "ok" | "failed";
  window_from: string | null;
  window_to: string | null;
  fetched: number;
  stored: number;
  unmatched: number;
  error: string | null;
}

interface UsageRow {
  id: string;
  kind: string;
  external_ref: string;
  occurred_at: string;
  quantity: number;
  pulled_at: string;
}

/**
 * Usage pulled from the carrier's call/SMS records (docs section 9A). The sync Edge Function runs every 6 hours; this
 * screen shows each run, lets admin run one now, and "Test read" fetches and maps the carrier's rows without saving
 * anything -- the way to check, before trusting the numbers, which records the carrier sends for an inbound call, an
 * outbound call and an SMS. Labelled "VoIP usage sync" (not "Crazytel") since the carrier is an implementation
 * detail -- the underlying tables keep their Crazytel-specific names.
 */
export function VoipUsage() {
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [syncStates, setSyncStates] = useState<Record<string, SyncStateRow>>({});
  const [businessId, setBusinessId] = useState("");
  const [usage, setUsage] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  useEffect(() => {
    Promise.all([
      supabase.from("businesses").select("id, name").order("name"),
      supabase.from("crazytel_sync_state").select("*"),
    ]).then(([{ data: biz }, { data: sync }]) => {
      const list = (biz ?? []) as BusinessRow[];
      setBusinesses(list);
      if (list[0]) setBusinessId(list[0].id);
      const map: Record<string, SyncStateRow> = {};
      for (const s of (sync ?? []) as SyncStateRow[]) map[s.business_id] = s;
      setSyncStates(map);
    });
  }, []);

  async function loadRuns() {
    const { data } = await supabase.from("crazytel_sync_runs").select("*").order("started_at", { ascending: false }).limit(10);
    setRuns((data ?? []) as RunRow[]);
  }

  useEffect(() => {
    void loadRuns();
  }, []);

  // Dates are whole Sydney days, "to" inclusive; the function accepts at most 90 days.
  const rangeDays = rangeFrom && rangeTo ? (Date.parse(nextDay(rangeTo)) - Date.parse(rangeFrom)) / 86400_000 : 0;
  const rangeError = !rangeFrom || !rangeTo ? null : rangeDays <= 0 ? "The end date is before the start date." : rangeDays > 90 ? "A range can be at most 90 days." : null;
  const rangeReady = Boolean(rangeFrom && rangeTo) && !rangeError;

  async function runSync(dryRun: boolean, range = false, probeV2 = false) {
    setBusy(true);
    setResult(null);
    const body: Record<string, unknown> = dryRun ? { dry_run: true, ...(probeV2 ? { probe_v2: true } : {}) } : {};
    if (range) {
      body.from = sydneyMidnightIso(rangeFrom);
      body.to = sydneyMidnightIso(nextDay(rangeTo));
    }
    const res = await callFunction("sync-crazytel-usage", body);
    setResult(res.ok ? JSON.stringify(res.data, null, 2) : `Failed: ${res.error}`);
    setBusy(false);
    await loadRuns();
  }

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    supabase
      .from("carrier_usage_records")
      .select("id, kind, external_ref, occurred_at, quantity, pulled_at")
      .eq("business_id", businessId)
      .order("occurred_at", { ascending: false })
      .limit(100)
      .then(({ data }) => {
        setUsage((data ?? []) as UsageRow[]);
        setLoading(false);
      });
  }, [businessId]);

  const state = syncStates[businessId];

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>VoIP usage sync</h1>

      <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
        Business
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4 }}>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>

      <div style={{ marginBottom: 24, fontSize: 14 }}>
        {state ? (
          <>
            Last synced: {state.last_synced_at ? formatDateTime(state.last_synced_at) : "never"} —{" "}
            <span style={{ color: state.last_sync_status === "failed" ? "var(--rc-danger)" : "var(--rc-success)" }}>{state.last_sync_status ?? "unknown"}</span>
            {state.last_sync_error && <div style={{ color: "var(--rc-danger)", marginTop: 4 }}>{state.last_sync_error}</div>}
          </>
        ) : (
          <span style={{ color: "var(--rc-text-faint)" }}>This business has never synced.</span>
        )}
      </div>

      <h2 className="rc-h2" style={{ marginBottom: 12 }}>Sync runs</h2>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button onClick={() => void runSync(false)} disabled={busy} style={{ padding: "6px 12px" }}>
          {busy ? "Working..." : "Sync now"}
        </button>
        <button onClick={() => void runSync(true)} disabled={busy} style={{ padding: "6px 12px" }}>
          Test read (saves nothing)
        </button>
      </div>
      <fieldset style={{ border: "1px solid var(--rc-border)", borderRadius: 6, padding: 12, margin: "0 0 12px" }}>
        <legend style={{ fontSize: 13, color: "var(--rc-text-muted)" }}>Sync a date range (Sydney days, up to 90)</legend>
        <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", fontSize: 14 }}>
          <label>
            From
            <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} style={{ display: "block", padding: 6, marginTop: 4 }} />
          </label>
          <label>
            To (included)
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} style={{ display: "block", padding: 6, marginTop: 4 }} />
          </label>
          <button onClick={() => void runSync(false, true)} disabled={busy || !rangeReady} style={{ padding: "6px 12px" }}>
            Sync range
          </button>
          <button onClick={() => void runSync(true, true)} disabled={busy || !rangeReady} style={{ padding: "6px 12px" }}>
            Test read range
          </button>
          <button onClick={() => void runSync(true, true, true)} disabled={busy || !rangeReady} style={{ padding: "6px 12px" }} title="Tries Crazytel's newer records endpoint with our key and shows what it returns (rows, product names). Saves nothing.">
            Probe newer records API
          </button>
        </div>
        {rangeError && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: "8px 0 0" }}>{rangeError}</p>}
        <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: "8px 0 0" }}>
          Re-reading a range is safe: records already saved are updated, never duplicated. A range run is listed below but does not count as the
          latest sync, so it does not unblock invoicing; use Sync now for that.
        </p>
      </fieldset>
      {result && (
        <pre style={{ fontSize: 12, background: "var(--rc-surface-sunken)", padding: 10, borderRadius: 6, overflow: "auto", maxHeight: 320 }}>{result}</pre>
      )}
      <div className="rc-table-scroll" style={{ marginBottom: 24 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: 6 }}>Started</th>
              <th style={{ padding: 6 }}>By</th>
              <th style={{ padding: 6 }}>Window</th>
              <th style={{ padding: 6 }}>Status</th>
              <th style={{ padding: 6 }}>Read</th>
              <th style={{ padding: 6 }}>Saved</th>
              <th style={{ padding: 6 }}>Unmatched</th>
              <th style={{ padding: 6 }}>Note</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)", verticalAlign: "top" }}>
                <td style={{ padding: 6, whiteSpace: "nowrap" }}>{formatDateTime(r.started_at)}</td>
                <td style={{ padding: 6 }}>{r.triggered_by}{r.custom_range ? " (range)" : ""}</td>
                <td style={{ padding: 6, whiteSpace: "nowrap" }}>
                  {r.window_from ? formatDate(r.window_from) : ""} – {r.window_to ? formatDate(r.window_to) : ""}
                </td>
                <td style={{ padding: 6, color: r.status === "failed" ? "var(--rc-danger)" : r.status === "ok" ? "var(--rc-success)" : "var(--rc-text-muted)" }}>{r.status}</td>
                <td style={{ padding: 6 }}>{r.fetched}</td>
                <td style={{ padding: 6 }}>{r.stored}</td>
                <td style={{ padding: 6 }}>{r.unmatched}</td>
                <td style={{ padding: 6, color: "var(--rc-text-muted)" }}>{r.error}</td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 12, color: "var(--rc-text-faint)" }}>
                  No sync has run yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="rc-h2" style={{ marginBottom: 12 }}>Recent usage records</h2>
      {loading ? (
        <p className="rc-loading">Loading...</p>
      ) : (
        <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Kind</th>
              <th style={{ padding: "10px 12px" }}>Occurred</th>
              <th style={{ padding: "10px 12px" }}>Quantity</th>
              <th style={{ padding: "10px 12px" }}>External ref</th>
              <th style={{ padding: "10px 12px" }}>Pulled at</th>
            </tr>
          </thead>
          <tbody>
            {usage.map((u) => (
              <tr key={u.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>{u.kind}</td>
                <td style={{ padding: "10px 12px" }}>{formatDateTime(u.occurred_at)}</td>
                <td style={{ padding: "10px 12px" }}>{u.quantity}</td>
                <td style={{ padding: "10px 12px", color: "var(--rc-text-faint)" }}>{u.external_ref}</td>
                <td style={{ padding: "10px 12px" }}>{formatDateTime(u.pulled_at)}</td>
              </tr>
            ))}
            {usage.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No usage records yet.
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
